import { IBaseModelDataModel, createBaseModelDataModel } from './base-model.data-model';
import { CustomData } from './field2.data-model';

export enum RoundSource {
  aumni = 'aumni',
  foresight = 'foresight',
  systemGenerated = 'system_generated',
}

// Round.type vocabulary — mirrors the API's deriveInstrumentType output. The round form
// offers only equity/safe/note; warrant and token are tracker-derived values.
export enum RoundInstrumentType {
  equity = 'Equity',
  safe = 'SAFE',
  note = 'Note',
  warrant = 'Warrant',
  token = 'Token',
}

export const NON_EQUITY_ROUND_TYPES: readonly string[] = [RoundInstrumentType.safe, RoundInstrumentType.note];

export function isNonEquityRoundType(type: string | null | undefined): boolean {
  return type != null && NON_EQUITY_ROUND_TYPES.includes(type);
}

const CONVERTIBLE_TRANSACTION_TYPES: readonly string[] = ['safe', 'note issuance', 'convertible note'];

// Aumni sets the round type; deal docs only the transaction type, matched exactly so "SAFE Conversion" stays equity.
export function isConvertibleRound(round: Pick<IFinanceRoundDataModel, 'type' | 'transactionType'>): boolean {
  return (
    isNonEquityRoundType(round.type) ||
    CONVERTIBLE_TRANSACTION_TYPES.includes((round.transactionType ?? '').toLowerCase())
  );
}

/**
 * A security issued in a round (round-wide, all holders), from `round.dealSecurities`
 * on `GET /round`. Populated by the backend from deal-doc staging (precise) with an
 * Aumni fallback — same shape either way.
 */
export interface DealSecurityJson {
  securityName: string | null;
  sharesIssued: number | null;
  pricePerShare: number | null;
  securityType?: string | null;
  issueDate?: string | null;
}

export interface IFinanceRoundDataModel extends IBaseModelDataModel {
  coInvestors?: string[] | null;
  companyId: number;
  customData?: CustomData | null;
  dealSecurities?: DealSecurityJson[] | null;
  fullyDilutedShares: number | null;
  funds?: string[] | null;
  id: number;
  initialQSBSQualification: boolean;
  leadInvestor?: string | null;
  leadInvestors?: string[] | null;
  name?: string | null;
  ownershipPercentage?: number | null;
  postMoneyValuation: number | null;
  preMoneyValuation?: number | null;
  pricePerShare?: number | null;
  newIssuancePPS?: number | null;
  raiseAmount: number | null;
  roundDate: string;
  roundDilution?: number | null;
  roundId: number | null;
  security?: string | null;
  source?: RoundSource | null;
  newSharesIssued?: number | null;
  subsequentClosingWindow?: number | null;
  fundsRecentTransaction?: string | null;
  totalHolders?: number | null;
  totalCost?: number | null;
  totalShares?: number | null;
  transactionType?: string | null;
  type?: string | null;
  // Hand-entered note terms (SAFE/convertible note rounds). Rates are whole percent
  // (8 = 8%); null means "no such term".
  manualValuationCap?: number | null;
  manualInterestRate?: number | null;
  manualDiscountRate?: number | null;
}

/**
 * Round shape for the Deal Docs sidebar RoundCard: the base finance round plus
 * the finance round id (for document operations) and the investment round
 * reference (for the round chip).
 */
export interface IRoundCardDisplayModel extends IFinanceRoundDataModel {
  roundId: number; // Finance round ID - Required for document management
  investmentRoundId?: number | null; // Investment round reference - For chip display
}

export function createFinanceRoundDataModel(
  overrides?: Partial<IFinanceRoundDataModel>
): IFinanceRoundDataModel {
  return {
    id: -1,
    ...createBaseModelDataModel(),
    companyId: 0,
    name: '',
    roundId: null,
    dealSecurities: null,
    postMoneyValuation: null,
    raiseAmount: null,
    fullyDilutedShares: null,
    initialQSBSQualification: false,
    leadInvestor: null,
    coInvestors: null,
    roundDate: '',
    source: null,
    manualValuationCap: null,
    manualInterestRate: null,
    manualDiscountRate: null,
    ...overrides,
  };
}
